import { memo, useMemo } from 'react'
import { FormHelperText } from '@mui/material'
import { useSelector } from 'react-redux'
import { Broker_OrderType, TransactionType } from '../../proto/generated/order'
import { selectQuickTradeQuoteSignature } from '../../selectors/security'
import { parseQuickTradeQuoteSignature, quoteReferenceFromScalars } from '../../util/quickTradeQuote'

export type QuickTradeSlPriceHintProps = {
    securityKey: string
    kind: 'stopLoss' | 'target'
    transactionType: TransactionType
    brokerOrderType: Broker_OrderType
    price: number
    priceMultiplier: number
    addEnabled: boolean
    value: number | string
    valueType: string | undefined
    existingLegPrice?: number
}

const QuickTradeSlPriceHint: React.FC<QuickTradeSlPriceHintProps> = ({
    securityKey,
    kind,
    transactionType,
    brokerOrderType,
    price,
    priceMultiplier,
    addEnabled,
    value,
    valueType,
    existingLegPrice,
}) => {
    const signature = useSelector(selectQuickTradeQuoteSignature(securityKey))
    const displayPrice = useMemo(() => {
        if (!addEnabled || !value || valueType === 'mtm') return undefined
        if (existingLegPrice != null) return existingLegPrice

        const quote = parseQuickTradeQuoteSignature(signature)
        const limitOrder = [Broker_OrderType.LIMIT_ORDER, Broker_OrderType.STOPLOSS_ORDER].includes(brokerOrderType)
        const quotePx = quoteReferenceFromScalars(transactionType, quote)
        const basePrice = limitOrder ? price : quotePx > 0 ? quotePx : price
        const pm = priceMultiplier || 100

        if (kind === 'stopLoss') {
            const sL = valueType === 'points' ? Number(value) : (price / pm) * Number(value) / 100
            return transactionType === TransactionType.BUY
                ? basePrice / pm - sL
                : basePrice / pm + sL
        }

        const tg = valueType === 'points' ? Number(value) : (price / pm) * Number(value) / 100
        return transactionType === TransactionType.BUY
            ? basePrice / pm + tg
            : basePrice / pm - tg
    }, [
        addEnabled,
        value,
        valueType,
        existingLegPrice,
        signature,
        transactionType,
        brokerOrderType,
        price,
        priceMultiplier,
        kind,
    ])

    if (displayPrice == null) return null

    const label = kind === 'stopLoss' ? 'SL Price' : 'Tgt Price'
    return <FormHelperText>{label}: {displayPrice.toFixed(2)}</FormHelperText>
}

export default memo(QuickTradeSlPriceHint)
